<template>
  <div class="page-wrap">
    <el-alert
      :title="isTeacherMode ? '管理本学科的基础数据：章节、学期/考试类型/地区/学年字典，以及题型标签。学科已锁定为任教学科。' : '在这里管理基础数据字典，选择题库管理中的下拉选项来源于此处。'"
      type="info"
      show-icon
      :closable="false"
      style="margin-bottom:16px"
    />

    <!-- 全局筛选：学科 + 学段 -->
    <el-row :gutter="16" style="margin-bottom:16px">
      <el-col :span="24">
        <el-card shadow="never" style="background:#f5f7fa">
          <span style="margin-right:8px;font-weight:bold">学科：</span>
          <el-tag v-if="isTeacherMode && teacherSubjectName" type="info" style="margin-right:24px">{{ teacherSubjectName }}</el-tag>
          <el-select v-else v-model="globalSubjectId" placeholder="请选择学科" style="width:150px;margin-right:24px" @change="onGlobalSubjectChange" clearable>
            <el-option v-for="s in subjects" :key="s.id" :label="s.name" :value="s.id" />
          </el-select>
          <span style="margin-right:8px;font-weight:bold">学段：</span>
          <el-select v-model="globalStageId" placeholder="请选择学段" style="width:150px;margin-right:24px" @change="onGlobalStageChange" clearable :disabled="!globalSubjectId">
            <el-option v-for="s in filteredStages" :key="s.id" :label="s.name" :value="s.id" />
          </el-select>
          <span v-if="!isTeacherMode && !globalSubjectId" style="color:#999;font-size:12px">请先选择学科</span>
          <el-button v-if="!isTeacherMode" type="text" @click="clearGlobalFilter">清除筛选</el-button>
        </el-card>
      </el-col>
    </el-row>

    <!-- 类型选择 -->
    <el-row :gutter="16" style="margin-bottom:16px">
      <el-col :span="24">
        <el-radio-group v-model="currentType" size="medium" @change="onTypeChange">
          <el-radio-button v-if="!isTeacherMode" label="subject">学科</el-radio-button>
          <el-radio-button v-if="!isTeacherMode" label="stage">学段</el-radio-button>
          <el-radio-button v-if="!isTeacherMode" label="grade">年级</el-radio-button>
          <el-radio-button label="semester">学期</el-radio-button>
          <el-radio-button label="exam_type">考试类型</el-radio-button>
          <el-radio-button label="region">地区</el-radio-button>
          <el-radio-button label="school_year">学年</el-radio-button>
          <el-radio-button label="knowledge">章节标签</el-radio-button>
          <el-radio-button label="question">题型标签</el-radio-button>
        </el-radio-group>
      </el-col>
    </el-row>

    <!-- 普通字典列表（非三级标签、非地区树） -->
    <template v-if="!isTagType && !isRegionType">
      <!-- 学段管理：全局共享，不依赖学科 -->
      <template v-if="currentType === 'stage'">
        <el-alert
          title="学段数据全局共享，所有学科共用相同的学段（小学/初中/高中）"
          type="info"
          show-icon
          :closable="false"
          style="margin-bottom:16px"
        />
      </template>

      <!-- 年级/考试类型等：需要先选择学段 -->
      <template v-if="needsStageId">
        <el-alert
          v-if="!globalStageId"
          :title="`请先选择学段，再管理${typeNameMap[currentType]}数据`"
          type="warning"
          show-icon
          :closable="false"
          style="margin-bottom:16px"
        />
        <el-alert
          v-else
          :title="`当前显示：${getStageName(globalStageId)} 的${typeNameMap[currentType]}数据`"
          type="info"
          show-icon
          :closable="false"
          style="margin-bottom:16px"
        />
      </template>

      <el-row style="margin-bottom:12px">
        <el-col :span="24">
          <el-button
            type="primary"
            icon="el-icon-plus"
            @click="openDialog()"
            :disabled="needsStageId && !globalStageId"
          >
            新增{{ typeNameMap[currentType] }}
          </el-button>
        </el-col>
      </el-row>
      <el-card shadow="never">
        <el-table :data="dictList" border stripe size="small" v-loading="loading">
          <el-table-column label="名称" prop="name" />
          <!-- 学段不再显示所属学科列，因为学段是全局共享的 -->
          <el-table-column v-if="currentType === 'grade'" label="所属学段" width="120" align="center">
            <template #default="{ row }">
              <el-tag size="mini" type="info">{{ getStageName(row.stageId) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="排序" prop="sortNo" width="80" align="center" />
          <el-table-column v-if="currentType === 'paper'" label="题目数" width="90" align="center">
            <template #default="{ row }">
              <el-tag size="mini" :type="(paperQuestionCounts[row.name] || 0) > 0 ? 'success' : 'info'">
                {{ paperQuestionCounts[row.name] || 0 }} 题
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" :width="currentType === 'paper' ? 180 : 120" align="center">
            <template #default="{ row }">
              <el-button v-if="currentType === 'paper'" type="text" icon="el-icon-view" @click="viewPaperQuestions(row)">查看题目</el-button>
              <el-button type="text" @click="openDialog(row)">编辑</el-button>
              <el-button type="text" class="danger-text" @click="deleteRow(row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-card>
    </template>

    <!-- 三级标签树管理面板 -->
    <template v-else-if="isTagType">
      <div class="tag-tree-panel">
        <div class="tag-tree-tip">
          <i class="el-icon-info" style="color:#1677ff;margin-right:4px"></i>
          <span v-if="globalSubjectId && globalStageId">
            当前显示：{{ getSubjectName(globalSubjectId) }} - {{ getStageName(globalStageId) }} 的{{ currentType === 'knowledge' ? '章节标签' : '题型标签' }}
          </span>
          <span v-else style="color:#f56c6c">
            请先选择学科和学段，标签将按学科+学段隔离管理
          </span>
        </div>

        <el-row :gutter="12" class="tag-cols">
          <!-- 第一级 -->
          <el-col :span="8">
            <el-card shadow="never" class="tag-col-card">
              <div class="tag-col-header">
                <span class="tag-col-title">{{ tagLevelLabel(1) }}</span>
                <el-button 
                  type="primary" 
                  size="mini" 
                  icon="el-icon-plus" 
                  :disabled="!canEditTags"
                  @click="openTagDialog(1, 0)"
                >新增</el-button>
              </div>
              <div v-loading="tagLoading" class="tag-col-body">
                <el-empty v-if="!tagLoading && level1List.length === 0" description="暂无数据" :image-size="60" />
                <div
                  v-for="item in level1List"
                  :key="item.id"
                  class="tag-item"
                  :class="{ 'tag-item-active': selectedL1 && selectedL1.id === item.id }"
                  @click="selectL1(item)"
                >
                  <span class="tag-item-name">{{ item.name }}</span>
                  <span class="tag-item-sort" v-if="level2Map[item.id] && level2Map[item.id].length">
                    <el-tag size="mini" type="info">{{ level2Map[item.id].length }}</el-tag>
                  </span>
                  <span class="tag-item-actions">
                    <i class="el-icon-edit" @click.stop="openTagDialog(1, 0, item)"></i>
                    <i class="el-icon-delete danger-icon" @click.stop="deleteTag(item)"></i>
                  </span>
                  <i v-if="selectedL1 && selectedL1.id === item.id" class="el-icon-arrow-right tag-arrow"></i>
                </div>
              </div>
            </el-card>
          </el-col>

          <!-- 第二级 -->
          <el-col :span="8">
            <el-card shadow="never" class="tag-col-card">
              <div class="tag-col-header">
                <span class="tag-col-title">
                  {{ tagLevelLabel(2) }}
                  <span v-if="selectedL1" class="tag-breadcrumb">{{ selectedL1.name }}</span>
                </span>
                <el-button
                  type="primary" size="mini" icon="el-icon-plus"
                  :disabled="!selectedL1 || !canEditTags"
                  @click="openTagDialog(2, selectedL1 && selectedL1.id)"
                >新增</el-button>
              </div>
              <div class="tag-col-body">
                <el-empty v-if="!selectedL1" :description="'请先选择' + tagLevelLabel(1)" :image-size="60" />
                <el-empty v-else-if="level2List.length === 0" description="暂无数据" :image-size="60" />
                <div
                  v-for="item in level2List"
                  :key="item.id"
                  class="tag-item"
                  :class="{ 'tag-item-active': selectedL2 && selectedL2.id === item.id }"
                  @click="selectL2(item)"
                >
                  <span class="tag-item-name">{{ item.name }}</span>
                  <span class="tag-item-sort" v-if="level3Map[item.id] && level3Map[item.id].length">
                    <el-tag size="mini" type="info">{{ level3Map[item.id].length }}</el-tag>
                  </span>
                  <span class="tag-item-actions">
                    <i class="el-icon-edit" @click.stop="openTagDialog(2, selectedL1 && selectedL1.id, item)"></i>
                    <i class="el-icon-delete danger-icon" @click.stop="deleteTag(item)"></i>
                  </span>
                  <i v-if="selectedL2 && selectedL2.id === item.id" class="el-icon-arrow-right tag-arrow"></i>
                </div>
              </div>
            </el-card>
          </el-col>

          <!-- 第三级 -->
          <el-col :span="8">
            <el-card shadow="never" class="tag-col-card">
              <div class="tag-col-header">
                <span class="tag-col-title">
                  {{ tagLevelLabel(3) }}
                  <span v-if="selectedL2" class="tag-breadcrumb">{{ selectedL2.name }}</span>
                </span>
                <el-button
                  type="primary" size="mini" icon="el-icon-plus"
                  :disabled="!selectedL2 || !canEditTags"
                  @click="openTagDialog(3, selectedL2 && selectedL2.id)"
                >新增</el-button>
              </div>
              <div class="tag-col-body">
                <el-empty v-if="!selectedL2" :description="'请先选择' + tagLevelLabel(2)" :image-size="60" />
                <el-empty v-else-if="level3List.length === 0" description="暂无数据" :image-size="60" />
                <div
                  v-for="item in level3List"
                  :key="item.id"
                  class="tag-item"
                >
                  <span class="tag-item-name">{{ item.name }}</span>
                  <span class="tag-item-actions">
                    <i class="el-icon-edit" @click.stop="openTagDialog(3, selectedL2 && selectedL2.id, item)"></i>
                    <i class="el-icon-delete danger-icon" @click.stop="deleteTag(item)"></i>
                  </span>
                </div>
              </div>
            </el-card>
          </el-col>
        </el-row>
      </div>
    </template>

    <!-- 地区三级管理：省/全国 → 城市或城区 → 学校 -->
    <template v-else-if="isRegionType">
      <div class="tag-tree-panel">
        <div class="tag-tree-tip">
          <i class="el-icon-info" style="color:#1677ff;margin-right:4px"></i>
          管理地区三级结构：<strong>地区（省/全国）</strong> → <strong>城市或城区</strong> → <strong>学校</strong>
        </div>
        <el-row :gutter="12" class="tag-cols">
          <el-col :span="8">
            <el-card shadow="never" class="tag-col-card">
              <div class="tag-col-header">
                <span class="tag-col-title">地区（省/全国）</span>
                <el-button type="primary" size="mini" icon="el-icon-plus" @click="openRegionDialog(1, null)">新增</el-button>
              </div>
              <div v-loading="regionLoading" class="tag-col-body">
                <el-empty v-if="!regionLoading && regionL1List.length === 0" description="暂无数据" :image-size="60" />
                <div
                  v-for="item in regionL1List"
                  :key="item.id"
                  class="tag-item"
                  :class="{ 'tag-item-active': selectedRegionL1 && selectedRegionL1.id === item.id }"
                  @click="selectRegionL1(item)"
                >
                  <span class="tag-item-name">{{ item.name }}</span>
                  <span class="tag-item-sort" v-if="regionL2Map[item.id] && regionL2Map[item.id].length">
                    <el-tag size="mini" type="info">{{ regionL2Map[item.id].length }}</el-tag>
                  </span>
                  <span class="tag-item-actions">
                    <i class="el-icon-edit" @click.stop="openRegionDialog(1, null, item)"></i>
                    <i class="el-icon-delete danger-icon" @click.stop="deleteRegion(item)"></i>
                  </span>
                  <i v-if="selectedRegionL1 && selectedRegionL1.id === item.id" class="el-icon-arrow-right tag-arrow"></i>
                </div>
              </div>
            </el-card>
          </el-col>
          <el-col :span="8">
            <el-card shadow="never" class="tag-col-card">
              <div class="tag-col-header">
                <span class="tag-col-title">
                  城市或城区
                  <span v-if="selectedRegionL1" class="tag-breadcrumb">{{ selectedRegionL1.name }}</span>
                </span>
                <el-button
                  type="primary" size="mini" icon="el-icon-plus"
                  :disabled="!selectedRegionL1"
                  @click="openRegionDialog(2, selectedRegionL1 && selectedRegionL1.id)"
                >新增</el-button>
              </div>
              <div class="tag-col-body">
                <el-empty v-if="!selectedRegionL1" description="请先选择地区" :image-size="60" />
                <el-empty v-else-if="regionL2List.length === 0" description="暂无数据" :image-size="60" />
                <div
                  v-for="item in regionL2List"
                  :key="item.id"
                  class="tag-item"
                  :class="{ 'tag-item-active': selectedRegionL2 && selectedRegionL2.id === item.id }"
                  @click="selectRegionL2(item)"
                >
                  <span class="tag-item-name">{{ item.name }}</span>
                  <span class="tag-item-sort" v-if="regionL3Map[item.id] && regionL3Map[item.id].length">
                    <el-tag size="mini" type="info">{{ regionL3Map[item.id].length }}</el-tag>
                  </span>
                  <span class="tag-item-actions">
                    <i class="el-icon-edit" @click.stop="openRegionDialog(2, selectedRegionL1 && selectedRegionL1.id, item)"></i>
                    <i class="el-icon-delete danger-icon" @click.stop="deleteRegion(item)"></i>
                  </span>
                  <i v-if="selectedRegionL2 && selectedRegionL2.id === item.id" class="el-icon-arrow-right tag-arrow"></i>
                </div>
              </div>
            </el-card>
          </el-col>
          <el-col :span="8">
            <el-card shadow="never" class="tag-col-card">
              <div class="tag-col-header">
                <span class="tag-col-title">
                  学校
                  <span v-if="selectedRegionL2" class="tag-breadcrumb">{{ selectedRegionL2.name }}</span>
                </span>
                <el-button
                  type="primary" size="mini" icon="el-icon-plus"
                  :disabled="!selectedRegionL2"
                  @click="openRegionDialog(3, selectedRegionL2 && selectedRegionL2.id)"
                >新增</el-button>
              </div>
              <div class="tag-col-body">
                <el-empty v-if="!selectedRegionL2" description="请先选择城市/城区" :image-size="60" />
                <el-empty v-else-if="regionL3List.length === 0" description="暂无数据" :image-size="60" />
                <div v-for="item in regionL3List" :key="item.id" class="tag-item">
                  <span class="tag-item-name">{{ item.name }}</span>
                  <span class="tag-item-actions">
                    <i class="el-icon-edit" @click.stop="openRegionDialog(3, selectedRegionL2 && selectedRegionL2.id, item)"></i>
                    <i class="el-icon-delete danger-icon" @click.stop="deleteRegion(item)"></i>
                  </span>
                </div>
              </div>
            </el-card>
          </el-col>
        </el-row>
      </div>
    </template>

    <!-- 普通字典弹窗 -->
    <el-dialog :title="form.id ? '编辑' + typeNameMap[currentType] : '新增' + typeNameMap[currentType]"
               :visible.sync="dialogVisible" width="400px">
      <el-form :model="form" ref="form" label-width="80px">
        <!-- 学段编辑：不再需要选择所属学科，学段是全局共享的 -->
        <el-form-item v-if="currentType === 'grade'" label="所属学段" :rules="[{required:true,message:'请选择学段'}]" prop="stageId">
          <el-select v-model="form.stageId" placeholder="请选择学段" style="width:100%" :disabled="!!form.id">
            <el-option v-for="s in stages" :key="s.id" :label="s.name" :value="s.id" />
          </el-select>
          <span v-if="form.id" style="color:#999;font-size:12px">创建后不可修改学段</span>
        </el-form-item>
        <el-form-item v-else-if="needsStageId" label="所属学段">
          <el-input :value="getStageName(form.stageId || globalStageId)" disabled />
        </el-form-item>
        <!-- 学科编辑：配置启用的学段 -->
        <el-form-item v-if="currentType === 'subject'" label="启用学段">
          <el-checkbox-group v-model="form.stageIds">
            <el-checkbox v-for="s in allStages" :key="s.id" :label="s.id">{{ s.name }}</el-checkbox>
          </el-checkbox-group>
          <span style="color:#999;font-size:12px">勾选该学科需要启用的学段，如生物不勾选小学</span>
        </el-form-item>
        <el-form-item label="名称" :rules="[{required:true,message:'请输入名称'}]" prop="name">
          <el-input v-model="form.name" :placeholder="'如：' + placeholderMap[currentType]" />
        </el-form-item>
        <el-form-item label="排序号">
          <el-input-number v-model="form.sortNo" :min="0" :max="999" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="save">保存</el-button>
      </template>
    </el-dialog>

    <!-- 三级标签节点弹窗 -->
    <el-dialog
      :title="tagForm.id ? ('编辑' + tagLevelLabel(tagForm.level)) : ('新增' + tagLevelLabel(tagForm.level))"
      :visible.sync="tagDialogVisible"
      width="400px"
    >
      <el-form :model="tagForm" ref="tagForm" label-width="80px">
        <el-form-item label="名称" :rules="[{required:true,message:'请输入名称'}]" prop="name">
          <el-input v-model="tagForm.name" placeholder="如：函数、增减性、易" />
        </el-form-item>
        <el-form-item label="排序号">
          <el-input-number v-model="tagForm.sortNo" :min="0" :max="999" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="tagDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="tagSaving" @click="saveTag">保存</el-button>
      </template>
    </el-dialog>

    <!-- 地区节点弹窗 -->
    <el-dialog
      :title="regionForm.id ? '编辑' + regionLevelLabel(regionForm.level) : '新增' + regionLevelLabel(regionForm.level)"
      :visible.sync="regionDialogVisible"
      width="400px"
    >
      <el-form :model="regionForm" ref="regionForm" label-width="80px">
        <el-form-item label="名称" :rules="[{required:true,message:'请输入名称'}]" prop="name">
          <el-input v-model="regionForm.name" :placeholder="regionPlaceholder(regionForm.level)" />
        </el-form-item>
        <el-form-item label="排序号">
          <el-input-number v-model="regionForm.sortNo" :min="0" :max="999" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="regionDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="regionSaving" @click="saveRegion">保存</el-button>
      </template>
    </el-dialog>

    <!-- 试卷题目抽屉 -->
    <el-drawer
      :title="drawerTitle"
      :visible.sync="drawerVisible"
      direction="rtl"
      size="600px"
    >
      <div style="padding:16px">
        <div v-if="drawerLoading" style="text-align:center;padding:40px">
          <i class="el-icon-loading" style="font-size:24px;color:#1677ff"></i>
          <div style="margin-top:8px;color:#999">加载中...</div>
        </div>
        <el-empty v-else-if="drawerQuestions.length === 0" description="该试卷还没有题目" />
        <div v-else>
          <div style="margin-bottom:12px;color:#666;font-size:13px">共 {{ drawerQuestions.length }} 道题目，按加入顺序排列</div>
          <div
            v-for="(q, idx) in drawerQuestions"
            :key="q.id"
            class="drawer-q-item"
          >
            <div class="drawer-q-num">{{ idx + 1 }}</div>
            <div class="drawer-q-body">
              <div class="drawer-q-text">{{ q.content }}</div>
              <div class="drawer-q-meta">
                <el-tag size="mini">{{ typeNameMap2[q.type] || q.type }}</el-tag>
                <el-tag v-if="q.questionTag" size="mini" type="info">{{ q.questionTag }}</el-tag>
                <span v-if="q.knowledgeTags" style="font-size:11px;color:#bbb;margin-left:6px">{{ q.knowledgeTags }}</span>
                <span style="font-size:11px;color:#bbb;margin-left:6px">答案：<strong>{{ q.answer }}</strong></span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import request from '../../utils/request'
import teacherRequest from '../../utils/teacherRequest'
import apiContext from '../../mixins/apiContext'

export default {
  name: 'BaseData',
  mixins: [apiContext],
  data() {
    return {
      currentType: 'knowledge',
      dictList: [],
      loading: false,
      dialogVisible: false,
      saving: false,
      form: { id: null, name: '', sortNo: 0, stageId: null },
      // 全局筛选
      globalSubjectId: null,
      globalStageId: null,
      stages: [],
      typeNameMap: {
        subject: '学科',
        stage: '学段',
        grade: '年级',
        semester: '学期',
        exam_type: '考试类型',
        region: '地区',
        school_year: '学年',
        knowledge: '章节标签',
        question: '题型标签'
      },
      placeholderMap: {
        subject: '数学',
        stage: '小学',
        grade: '高一',
        semester: '上学期',
        exam_type: '期中',
        region: '北京',
        school_year: '2024-2025'
      },
      // 所有学段（用于关联）
      allStages: [],
      // 所有年级（用于显示名称）
      grades: [],

      paperQuestionCounts: {},
      drawerVisible: false,
      drawerTitle: '',
      drawerLoading: false,
      drawerQuestions: [],
      typeNameMap2: { SINGLE: '单选题', MULTI: '多选题', FILL: '填空题', QA: '问答题' },

      // 三级标签树
      tagLoading: false,
      level1List: [],
      level2List: [],
      level3List: [],
      level2Map: {},  // {l1Id: [items]}，用于显示子节点数量角标
      level3Map: {},  // {l2Id: [items]}
      selectedL1: null,
      selectedL2: null,
      tagDialogVisible: false,
      tagSaving: false,
      tagForm: { id: null, level: 1, parentId: 0, name: '', sortNo: 0 },
      teacherSubjectName: '',
      // 地区三级树
      regionLoading: false,
      regionL1List: [],
      regionL2List: [],
      regionL3List: [],
      regionL2Map: {},
      regionL3Map: {},
      selectedRegionL1: null,
      selectedRegionL2: null,
      regionDialogVisible: false,
      regionSaving: false,
      regionForm: { id: null, level: 1, parentId: null, name: '', sortNo: 0 }
    }
  },
  computed: {
    isTagType() {
      return this.currentType === 'knowledge' || this.currentType === 'question'
    },
    isRegionType() {
      return this.currentType === 'region'
    },
    canEditTags() {
      return this.globalSubjectId && this.globalStageId
    },
    filteredStages() {
      if (!this.globalSubjectId) return []
      return this.stages
    },
    needsStageId() {
      return ['grade', 'exam_type', 'semester', 'school_year'].includes(this.currentType)
    }
  },
  created() {
    if (this.isTeacherMode) {
      teacherRequest.get('/me').then(me => {
        this.globalSubjectId = me.subjectId
        this.teacherSubjectName = me.subjectName || ''
        this.loadSubjects()
        this.loadStages(me.subjectId)
        this.loadData()
      }).catch(() => {})
    } else {
      this.currentType = 'subject'
      this.loadSubjects()
      this.loadStages()
      this.loadData()
    }
  },
  methods: {
    loadSubjects() {
      this.api.get('/dict', { params: { type: 'subject' } }).then(data => {
        this.subjects = data || []
      })
    },
    loadStages(subjectId) {
      // 学段全局共享，查询所有学段
      // 如果传入了学科ID或选择了学科，则查询该学科关联的学段
      const params = {}
      const sid = subjectId || this.globalSubjectId
      if (sid) {
        params.subjectId = sid
      }
      this.api.get('/stage', { params }).then(data => {
        this.stages = data || []
      })
    },
    getSubjectName(subjectId) {
      const subject = this.subjects.find(s => s.id === subjectId)
      return subject ? subject.name : ''
    },
    getStageName(stageId) {
      const stage = this.stages.find(s => s.id === stageId)
      return stage ? stage.name : ''
    },
    getGradeName(gradeId) {
      const grade = this.dictList.find(g => g.id === gradeId)
      return grade ? grade.name : ''
    },
    onGlobalSubjectChange() {
      // 清除学段选择
      this.globalStageId = null
      // 根据选择的学科加载关联的学段
      this.loadStages(this.globalSubjectId)
      // 刷新数据
      if (this.currentType === 'stage' || this.currentType === 'grade') {
        this.loadData()
      }
      // 学科变化时刷新标签树（标签按学科+学段隔离）
      if (this.isTagType) {
        this.loadTagTree()
      }
    },
    onGlobalStageChange() {
      // 学段变化时刷新标签树（标签按学科+学段隔离）
      if (this.isTagType) {
        this.loadTagTree()
      } else if (!this.isRegionType && this.currentType !== 'stage') {
        this.loadData()
      }
    },
    clearGlobalFilter() {
      this.globalSubjectId = null
      this.globalStageId = null
      if (this.isTagType) {
        this.loadTagTree()
      }
    },


    loadData() {
      if (this.isTagType) {
        this.loadTagTree()
      } else if (this.isRegionType) {
        this.loadRegionTree()
      } else if (this.currentType === 'stage') {
        // 学段使用单独API，按学科筛选
        this.loading = true
        const params = {}
        if (this.globalSubjectId) {
          params.subjectId = this.globalSubjectId
        }
        this.api.get('/stage', { params })
          .then(data => {
            this.dictList = data || []
          })
          .finally(() => { this.loading = false })
      } else {
        if (this.needsStageId && !this.globalStageId) {
          this.dictList = []
          return
        }
        this.loading = true
        const params = { type: this.currentType }
        if (this.needsStageId && this.globalStageId) {
          params.stageId = this.globalStageId
        }
        this.api.get('/dict', { params })
          .then(data => {
            this.dictList = data || []
          })
          .finally(() => { this.loading = false })
      }
    },
    onTypeChange() {
      this.selectedL1 = null
      this.selectedL2 = null
      this.level1List = []
      this.level2List = []
      this.level3List = []
      this.level2Map = {}
      this.level3Map = {}
      this.selectedRegionL1 = null
      this.selectedRegionL2 = null
      this.regionL1List = []
      this.regionL2List = []
      this.regionL3List = []
      this.regionL2Map = {}
      this.regionL3Map = {}
      this.loadData()
    },

    tagLevelLabel(level) {
      if (this.currentType === 'knowledge') {
        return { 1: '书标签', 2: '章标签', 3: '节标签' }[level] || '标签'
      }
      const prefix = { 1: '一级', 2: '二级', 3: '三级' }[level] || ''
      return prefix + '标签'
    },

    // ===== 三级标签树 =====
    loadTagTree() {
      this.tagLoading = true
      const params = { tagType: this.currentType }
      // 标签按学科+学段双重隔离
      if (this.globalSubjectId) params.subjectId = this.globalSubjectId
      if (this.globalStageId) params.stageId = this.globalStageId
      this.api.get('/tag-tree', { params })
        .then(tree => {
          this.level1List = tree || []
          // 构建 level2Map / level3Map 用于显示角标
          const l2Map = {}
          const l3Map = {}
          for (const l1 of this.level1List) {
            l2Map[l1.id] = l1.children || []
            for (const l2 of (l1.children || [])) {
              l3Map[l2.id] = l2.children || []
            }
          }
          this.level2Map = l2Map
          this.level3Map = l3Map

          // 如果之前有选中的节点，尝试恢复
          if (this.selectedL1) {
            const found = this.level1List.find(i => i.id === this.selectedL1.id)
            if (found) {
              this.selectedL1 = found
              this.level2List = found.children || []
              if (this.selectedL2) {
                const foundL2 = this.level2List.find(i => i.id === this.selectedL2.id)
                if (foundL2) {
                  this.selectedL2 = foundL2
                  this.level3List = foundL2.children || []
                } else {
                  this.selectedL2 = null
                  this.level3List = []
                }
              }
            } else {
              this.selectedL1 = null
              this.selectedL2 = null
              this.level2List = []
              this.level3List = []
            }
          }
        })
        .finally(() => { this.tagLoading = false })
    },
    selectL1(item) {
      this.selectedL1 = item
      this.selectedL2 = null
      this.level2List = item.children || []
      this.level3List = []
    },
    selectL2(item) {
      this.selectedL2 = item
      this.level3List = item.children || []
    },
    openTagDialog(level, parentId, editItem) {
      this.tagForm = editItem
        ? { id: editItem.id, level, parentId, name: editItem.name, sortNo: editItem.sortNo }
        : { id: null, level, parentId: parentId || 0, name: '', sortNo: 0 }
      this.tagDialogVisible = true
      this.$nextTick(() => { this.$refs.tagForm && this.$refs.tagForm.clearValidate() })
    },
    saveTag() {
      this.$refs.tagForm.validate(valid => {
        if (!valid) return
        if (!this.canEditTags) {
          this.$message.warning('请先选择学科和学段')
          return
        }
        this.tagSaving = true
        const { id, name, sortNo, level, parentId } = this.tagForm
        const req = id
          ? this.api.put('/tag-tree/' + id, { name, sortNo })
          : this.api.post('/tag-tree', { 
              tagType: this.currentType, 
              parentId, 
              name, 
              sortNo,
              subjectId: this.globalSubjectId,
              stageId: this.globalStageId
            })
        req.then(() => {
          this.$message.success('保存成功')
          this.tagDialogVisible = false
          this.loadTagTree()
        }).catch(err => {
          this.$message.error(err.message || '保存失败')
        }).finally(() => { this.tagSaving = false })
      })
    },
    deleteTag(item) {
      const hasChildren = (item.children && item.children.length > 0)
      const msg = hasChildren
        ? `确认删除【${item.name}】及其所有子标签？`
        : `确认删除【${item.name}】？`
      this.$confirm(msg, '警告', { type: 'warning' }).then(() => {
        this.api.delete('/tag-tree/' + item.id).then(() => {
          this.$message.success('已删除')
          this.loadTagTree()
        })
      }).catch(() => {})
    },

    // ===== 地区三级树 =====
    regionTypeByLevel(level) {
      return { 1: 'region', 2: 'region_city', 3: 'region_school' }[level]
    },
    regionLevelLabel(level) {
      return { 1: '地区', 2: '城市/城区', 3: '学校' }[level] || ''
    },
    regionPlaceholder(level) {
      return { 1: '如：北京', 2: '如：海淀区', 3: '如：XX中学' }[level] || ''
    },
    loadRegionTree() {
      this.regionLoading = true
      return this.api.get('/dict/region-tree')
        .then(tree => {
          this.regionL1List = tree || []
          const l2Map = {}
          const l3Map = {}
          for (const l1 of this.regionL1List) {
            l2Map[l1.id] = l1.children || []
            for (const l2 of (l1.children || [])) {
              l3Map[l2.id] = l2.children || []
            }
          }
          this.regionL2Map = l2Map
          this.regionL3Map = l3Map
          if (this.selectedRegionL1) {
            const found = this.regionL1List.find(i => i.id == this.selectedRegionL1.id)
            if (found) {
              this.selectedRegionL1 = found
              this.regionL2List = found.children || []
              if (this.selectedRegionL2) {
                const foundL2 = this.regionL2List.find(i => i.id == this.selectedRegionL2.id)
                if (foundL2) {
                  this.selectedRegionL2 = foundL2
                  this.regionL3List = foundL2.children || []
                } else {
                  this.selectedRegionL2 = null
                  this.regionL3List = []
                }
              }
            } else {
              this.selectedRegionL1 = null
              this.selectedRegionL2 = null
              this.regionL2List = []
              this.regionL3List = []
            }
          }
        })
        .finally(() => { this.regionLoading = false })
    },
    selectRegionL1(item) {
      this.selectedRegionL1 = item
      this.selectedRegionL2 = null
      this.regionL2List = item.children || []
      this.regionL3List = []
    },
    selectRegionL2(item) {
      this.selectedRegionL2 = item
      this.regionL3List = item.children || []
    },
    openRegionDialog(level, parentId, editItem) {
      this.regionForm = editItem
        ? { id: editItem.id, level, parentId, name: editItem.name, sortNo: editItem.sortNo }
        : { id: null, level, parentId: parentId || null, name: '', sortNo: 0 }
      this.regionDialogVisible = true
      this.$nextTick(() => { this.$refs.regionForm && this.$refs.regionForm.clearValidate() })
    },
    saveRegion() {
      this.$refs.regionForm.validate(valid => {
        if (!valid) return
        this.regionSaving = true
        const { id, name, sortNo, level, parentId } = this.regionForm
        const type = this.regionTypeByLevel(level)
        const req = id
          ? this.api.put('/dict/' + id, { name, sortNo })
          : this.api.post('/dict', { type, name, sortNo, parentId: level === 1 ? null : parentId })
        req.then(() => {
          this.$message.success('保存成功')
          this.regionDialogVisible = false
          const keepL1Id = this.selectedRegionL1 && this.selectedRegionL1.id
          const keepL2Id = this.selectedRegionL2 && this.selectedRegionL2.id
          this.loadRegionTree().then(() => {
            if (keepL1Id != null) {
              const l1 = this.regionL1List.find(i => i.id == keepL1Id)
              if (l1) this.selectRegionL1(l1)
            }
            if (keepL2Id != null) {
              const l2 = this.regionL2List.find(i => i.id == keepL2Id)
              if (l2) this.selectRegionL2(l2)
            }
          })
        }).catch(err => {
          this.$message.error(err.message || '保存失败')
        }).finally(() => { this.regionSaving = false })
      })
    },
    deleteRegion(item) {
      const hasChildren = (item.children && item.children.length > 0)
      const msg = hasChildren
        ? `确认删除【${item.name}】及其下级数据？`
        : `确认删除【${item.name}】？`
      this.$confirm(msg, '警告', { type: 'warning' }).then(() => {
        this.api.delete('/dict/' + item.id).then(() => {
          this.$message.success('已删除')
          this.loadRegionTree()
        })
      }).catch(() => {})
    },

    // ===== 普通字典 =====
    loadPaperQuestionCounts() {
      const counts = {}
      const tasks = this.dictList.map(p =>
        this.api.post('/paper/ensure', { name: p.name })
          .then(data => { counts[p.name] = data.questionCount || 0 })
          .catch(() => { counts[p.name] = 0 })
      )
      Promise.all(tasks).then(() => { this.paperQuestionCounts = { ...counts } })
    },
    viewPaperQuestions(row) {
      this.drawerTitle = `《${row.name}》的题目列表`
      this.drawerVisible = true
      this.drawerLoading = true
      this.drawerQuestions = []
      this.api.post('/paper/ensure', { name: row.name })
        .then(data => this.api.get(`/paper/${data.id}/questions`))
        .then(questions => { this.drawerQuestions = questions || [] })
        .catch(() => { this.drawerQuestions = [] })
        .finally(() => { this.drawerLoading = false })
    },
    openDialog(row) {
      // 如果是学科类型，先加载所有学段
      if (this.currentType === 'subject') {
        this.api.get('/stage').then(data => {
          this.allStages = data || []
        })
      }
      if (row) {
        // 编辑：根据类型设置表单数据
        this.form = { 
          id: row.id, 
          name: row.name, 
          sortNo: row.sortNo, 
          stageId: row.stageId || this.globalStageId,
          stageIds: row.stageIds || [] // 学科已启用的学段ID列表
        }
        // 如果是编辑学科，加载该学科的学段关联
        if (this.currentType === 'subject' && row.id) {
          this.api.get('/subject-stage', { params: { subjectId: row.id } }).then(data => {
            this.form.stageIds = (data || []).map(ss => ss.stageId)
          })
        }
      } else {
        // 新增：根据类型设置默认值
        this.form = { 
          id: null, 
          name: '', 
          sortNo: 0, 
          stageId: this.needsStageId ? this.globalStageId : null,
          stageIds: [] // 学科默认不选中任何学段
        }
      }
      this.dialogVisible = true
      this.$nextTick(() => { this.$refs.form && this.$refs.form.clearValidate() })
    },
    save() {
      this.$refs.form.validate(valid => {
        if (!valid) return
        this.saving = true
        // 学段使用单独API
        if (this.currentType === 'stage') {
          // 学段全局共享，不需要 subjectId
          const payload = { name: this.form.name, sortNo: this.form.sortNo }
          const req = this.form.id
            ? this.api.put('/stage/' + this.form.id, payload)
            : this.api.post('/stage', payload)
          req.then(() => {
            this.$message.success('保存成功')
            this.dialogVisible = false
            this.loadData()
            this.loadStages() // 刷新学段下拉列表
          }).finally(() => { this.saving = false })
        } else {
          if (this.needsStageId && !this.form.stageId && !this.globalStageId) {
            this.$message.warning('请先选择学段')
            this.saving = false
            return
          }
          const payload = { ...this.form, type: this.currentType }
          if (this.needsStageId) {
            payload.stageId = this.form.stageId || this.globalStageId
          }
          // 学科类型需要保存学段关联
          if (this.currentType === 'subject') {
            payload.stageIds = this.form.stageIds || []
          }
          const req = this.form.id
            ? this.api.put('/dict/' + this.form.id, payload)
            : this.api.post('/dict', payload)
          req.then(() => {
            this.$message.success('保存成功')
            this.dialogVisible = false
            this.loadData()
          }).finally(() => { this.saving = false })
        }
      })
    },
    deleteRow(row) {
      this.$confirm('确认删除【' + row.name + '】？', '警告', { type: 'warning' }).then(() => {
        // 学段使用单独API
        if (this.currentType === 'stage') {
          this.api.delete('/stage/' + row.id).then(() => {
            this.$message.success('已删除')
            this.loadData()
            this.loadStages() // 刷新学段下拉列表
          })
        } else {
          this.api.delete('/dict/' + row.id).then(() => {
            this.$message.success('已删除')
            this.loadData()
          })
        }
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.page-wrap {}
.danger-text { color: #ff4d4f; }

/* ===== 三级标签树 ===== */
.tag-tree-panel { }
.tag-tree-tip {
  background: #f0f7ff;
  border: 1px solid #bae0ff;
  border-radius: 6px;
  padding: 8px 14px;
  font-size: 13px;
  color: #333;
  margin-bottom: 14px;
}
.tag-cols { min-height: 420px; }
.tag-col-card { height: 100%; min-height: 380px; }
.tag-col-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
}
.tag-col-title {
  font-weight: 600;
  font-size: 14px;
  color: #333;
  display: flex;
  align-items: center;
  gap: 6px;
}
.tag-breadcrumb {
  font-weight: normal;
  font-size: 12px;
  color: #1677ff;
  background: #e6f4ff;
  padding: 1px 7px;
  border-radius: 10px;
}
.tag-col-body { }

.tag-item {
  display: flex;
  align-items: center;
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s;
  position: relative;
  margin-bottom: 2px;
  border: 1px solid transparent;
}
.tag-item:hover { background: #f5f5f5; }
.tag-item-active {
  background: #e6f4ff !important;
  border-color: #91caff !important;
}
.tag-item-name {
  flex: 1;
  font-size: 13px;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tag-item-sort { margin-right: 6px; }
.tag-item-actions {
  display: none;
  gap: 8px;
  align-items: center;
  color: #aaa;
  font-size: 14px;
}
.tag-item:hover .tag-item-actions { display: flex; }
.tag-item-actions i { cursor: pointer; transition: color 0.15s; }
.tag-item-actions i:hover { color: #1677ff; }
.danger-icon:hover { color: #ff4d4f !important; }
.tag-arrow {
  font-size: 12px;
  color: #1677ff;
  margin-left: 4px;
}

/* ===== 抽屉题目列表 ===== */
.drawer-q-item {
  display: flex;
  gap: 10px;
  padding: 12px 0;
  border-bottom: 1px solid #f5f5f5;
}
.drawer-q-item:last-child { border-bottom: none; }
.drawer-q-num {
  width: 24px;
  height: 24px;
  background: #1677ff;
  color: #fff;
  border-radius: 4px;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
}
.drawer-q-body { flex: 1; min-width: 0; }
.drawer-q-text {
  font-size: 13px;
  color: #333;
  line-height: 1.6;
  margin-bottom: 6px;
}
.drawer-q-meta { display: flex; align-items: center; flex-wrap: wrap; gap: 4px; }
</style>
